import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { Capacitor } from '@capacitor/core';
import {
  QrCode,
  Copy,
  Check,
  Download,
  Sparkles,
  AlertCircle,
  RefreshCw,
  User,
  KeyRound,
  BookOpen,
  Link2,
  CheckCircle2,
} from 'lucide-react';
import { Modal } from './Modal';
import { haptics } from '../lib/haptics';
import { sounds } from '../lib/sounds';
import { DOWNLOAD_LINKS } from '../lib/downloadLinks';
import { api } from '../lib/api';

interface EnrollmentQrModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    id: string;
    fullName: string;
    email: string;
    activationCode?: string | null;
  } | null;
  orgName?: string;
}

type TabType = 'code' | 'qr' | 'guide';

export function EnrollmentQrModal({ isOpen, onClose, user, orgName }: EnrollmentQrModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('code');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedJson, setCopiedJson] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Real enrollment state
  const [enrollmentCode, setEnrollmentCode] = useState<string>('');
  const [assignedUserName, setAssignedUserName] = useState<string>('');
  const [usersList, setUsersList] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const workstationName = `${(assignedUserName || user?.fullName || 'Workstation').split(' ')[0]}-PC`;

  const serverUrl =
    Capacitor.isNativePlatform() || window.location.hostname === 'localhost' || window.location.protocol === 'capacitor:' || window.location.origin.includes('localhost')
      ? 'https://ck-pulse.pages.dev'
      : window.location.origin;

  const enrollmentPayload = {
    server: serverUrl,
    code: enrollmentCode,
    name: workstationName,
    user: assignedUserName || user?.fullName || 'Employee',
    org: orgName || 'CK Pulse Workspace',
  };

  const jsonString = JSON.stringify(enrollmentPayload, null, 2);
  const deepLink = `ckpulse://enroll?server=${encodeURIComponent(serverUrl)}&code=${encodeURIComponent(enrollmentCode)}&name=${encodeURIComponent(workstationName)}`;

  // Fetch token from server
  const fetchToken = async (targetUserId?: string) => {
    setLoading(true);
    setError(null);
    try {
      let res: any;
      if (targetUserId && targetUserId !== 'device') {
        res = await api.admin.generateEnrollmentToken(targetUserId);
      } else if (user?.id && user.id !== 'device') {
        res = await api.admin.generateEnrollmentToken(user.id);
      } else {
        res = await api.admin.generateDeviceEnrollmentToken(targetUserId || undefined);
      }

      if (res && res.enrollmentToken) {
        const code = res.enrollmentToken.code;
        setEnrollmentCode(code);
        setAssignedUserName(res.enrollmentToken.userName || user?.fullName || 'Employee');
      } else {
        throw new Error('Server did not return a valid enrollment token.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate cryptographic workstation enrollment code.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    api.admin
      .getUsers()
      .then((res) => {
        if (res.users && res.users.length > 0) {
          setUsersList(res.users);
          if (user?.id && user.id !== 'device') {
            setSelectedUserId(user.id);
          } else {
            setSelectedUserId(res.users[0].id);
          }
        }
      })
      .catch(() => {});
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen) return;
    const uid = selectedUserId || (user?.id !== 'device' ? user?.id : undefined);
    fetchToken(uid);
  }, [isOpen, selectedUserId]);

  useEffect(() => {
    if (!enrollmentCode) {
      setQrDataUrl('');
      return;
    }

    QRCode.toDataURL(jsonString, {
      width: 320,
      margin: 1.5,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
      errorCorrectionLevel: 'M',
    })
      .then((url) => setQrDataUrl(url))
      .catch((err) => console.error('Failed to generate QR code', err));
  }, [enrollmentCode, assignedUserName, serverUrl]);

  const handleCopyCode = () => {
    if (!enrollmentCode) return;
    navigator.clipboard.writeText(enrollmentCode);
    setCopiedCode(true);
    haptics.notificationSuccess();
    sounds.playTapSound();
    setTimeout(() => setCopiedCode(false), 2500);
  };

  const handleCopyJson = () => {
    if (!enrollmentCode) return;
    navigator.clipboard.writeText(jsonString);
    setCopiedJson(true);
    haptics.notificationSuccess();
    sounds.playTapSound();
    setTimeout(() => setCopiedJson(false), 2500);
  };

  const handleCopyDeepLink = () => {
    if (!enrollmentCode) return;
    navigator.clipboard.writeText(deepLink);
    setCopiedLink(true);
    haptics.notificationSuccess();
    sounds.playTapSound();
    setTimeout(() => setCopiedLink(false), 2500);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Pair Workstation via QR" maxWidth="max-w-lg">
      <div className="space-y-4">
        {/* Banner */}
        <div className="p-3.5 rounded-2xl bg-gradient-to-br from-blue-600/15 via-indigo-600/10 to-transparent border border-blue-500/20 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shrink-0 shadow-lg shadow-blue-500/25">
            <QrCode className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-1.5">
              <span>Instant Workstation Pairing</span>
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            </h4>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
              Cryptographically bind <strong>CKPulse.Agent.exe</strong> to your organization without keylogging.
            </p>
          </div>
        </div>

        {/* Member Selector (if multiple exist) */}
        {usersList.length > 1 && (
          <div className="flex items-center justify-between gap-2 p-2.5 rounded-xl bg-zinc-100 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 text-xs">
            <span className="text-zinc-500 dark:text-zinc-400 shrink-0 font-medium flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-blue-500" />
              <span>Target Member:</span>
            </span>
            <select
              value={selectedUserId}
              onChange={(e) => {
                haptics.selection();
                setSelectedUserId(e.target.value);
              }}
              className="bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white rounded-lg px-2.5 py-1 text-xs border border-black/10 dark:border-white/10 focus:outline-none font-semibold truncate max-w-[220px]"
            >
              {usersList.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName} ({u.department || u.email})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Error Feedback */}
        {error && (
          <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-900/60 text-xs text-rose-300 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
              <span>{error}</span>
            </div>
            <button
              onClick={() => fetchToken(selectedUserId)}
              className="p-1 rounded bg-rose-900/50 hover:bg-rose-800 text-rose-200"
              title="Retry"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Tabs: Code vs QR vs Guide */}
        <div className="flex items-center p-1 rounded-xl bg-zinc-100 dark:bg-zinc-900 border border-black/5 dark:border-white/5 text-xs">
          <button
            onClick={() => {
              haptics.selection();
              setActiveTab('code');
            }}
            className={`flex-1 py-1.5 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition ${
              activeTab === 'code' ? 'bg-white dark:bg-zinc-800 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-zinc-500'
            }`}
          >
            <KeyRound className="w-3.5 h-3.5" />
            <span>1-Click Setup</span>
          </button>
          <button
            onClick={() => {
              haptics.selection();
              setActiveTab('qr');
            }}
            className={`flex-1 py-1.5 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition ${
              activeTab === 'qr' ? 'bg-white dark:bg-zinc-800 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-zinc-500'
            }`}
          >
            <QrCode className="w-3.5 h-3.5" />
            <span>QR Scanner</span>
          </button>
          <button
            onClick={() => {
              haptics.selection();
              setActiveTab('guide');
            }}
            className={`flex-1 py-1.5 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition ${
              activeTab === 'guide' ? 'bg-white dark:bg-zinc-800 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-zinc-500'
            }`}
          >
            <BookOpen className="w-3.5 h-3.5" />
            <span>Step-by-Step Guide</span>
          </button>
        </div>

        {/* Tab 1: 1-Click Setup */}
        {activeTab === 'code' && (
          <div className="space-y-3 animate-ios-enter">
            <div className="p-4 rounded-2xl bg-zinc-100 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">One-Time Pairing Code</span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-bold">
                  Valid for 24h
                </span>
              </div>

              <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white dark:bg-zinc-950 border border-black/10 dark:border-white/10 shadow-inner">
                <span className="text-xl font-mono font-black text-blue-600 dark:text-blue-400 tracking-widest">
                  {loading ? '••••••••••' : enrollmentCode || 'UNAVAILABLE'}
                </span>
                <button
                  onClick={handleCopyCode}
                  disabled={!enrollmentCode}
                  className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 active:scale-95 text-xs font-bold text-white flex items-center gap-1.5 transition shadow-sm disabled:opacity-40"
                >
                  {copiedCode ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedCode ? 'Copied' : 'Copy'}</span>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                <div>
                  <span className="text-zinc-400 block text-[11px]">Workstation Display Name</span>
                  <span className="font-semibold text-zinc-800 dark:text-zinc-200">{workstationName}</span>
                </div>
                <div>
                  <span className="text-zinc-400 block text-[11px]">Assigned Employee</span>
                  <span className="font-semibold text-zinc-800 dark:text-zinc-200">
                    {assignedUserName || user?.fullName || 'General Workstation'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row items-center gap-2">
              <button
                onClick={handleCopyJson}
                disabled={!enrollmentCode}
                className="w-full sm:flex-1 py-2.5 px-3 rounded-xl bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 hover:opacity-90 active:scale-95 text-xs font-bold flex items-center justify-center gap-2 transition shadow-md disabled:opacity-40"
              >
                {copiedJson ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 dark:text-emerald-600" />
                    <span>Setup Payload Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy Setup Data for Agent</span>
                  </>
                )}
              </button>

              <button
                onClick={handleCopyDeepLink}
                disabled={!enrollmentCode}
                className="w-full sm:w-auto py-2.5 px-3 rounded-xl bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-xs font-semibold text-zinc-700 dark:text-zinc-300 border border-black/10 dark:border-white/10 flex items-center justify-center gap-1.5 transition"
                title="Copy Deep Link Scheme"
              >
                {copiedLink ? <Check className="w-4 h-4 text-emerald-500" /> : <Link2 className="w-4 h-4" />}
                <span>{copiedLink ? 'Link Copied' : 'Deep Link'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Tab 2: QR Scanner */}
        {activeTab === 'qr' && (
          <div className="flex flex-col items-center justify-center py-2 space-y-3 animate-ios-enter">
            {/* White Matte Container for Pristine Camera Scanning */}
            <div className="p-4 rounded-3xl bg-white border-2 border-blue-500/20 shadow-2xl shadow-blue-500/10 flex flex-col items-center">
              {loading ? (
                <div className="w-56 h-56 flex flex-col items-center justify-center bg-zinc-50 rounded-2xl gap-2">
                  <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-[11px] text-zinc-400 font-mono">Generating token...</span>
                </div>
              ) : qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="Workstation Enrollment QR"
                  className="w-56 h-56 object-contain rounded-xl"
                />
              ) : (
                <div className="w-56 h-56 flex items-center justify-center bg-zinc-100 rounded-xl text-xs text-zinc-400 font-mono">
                  Token unavailable
                </div>
              )}
              <span className="text-[10px] font-mono text-zinc-500 font-bold uppercase tracking-wider mt-2">
                Scan with scanner or phone camera
              </span>
            </div>

            <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center max-w-sm">
              Encodes self-describing workstation connection parameters, pairing code, and server API origin.
            </p>
          </div>
        )}

        {/* Tab 3: Step-by-Step Guide */}
        {activeTab === 'guide' && (
          <div className="p-4 rounded-2xl bg-zinc-100 dark:bg-zinc-900/80 border border-black/5 dark:border-white/5 space-y-3 animate-ios-enter">
            <h5 className="text-xs font-bold text-zinc-900 dark:text-white uppercase tracking-wider">
              3-Step Workstation Enrollment Walkthrough:
            </h5>
            <ol className="text-xs text-zinc-600 dark:text-zinc-400 space-y-2.5 list-decimal list-inside">
              <li className="leading-relaxed">
                <strong>Download & Launch:</strong> Download <strong>CKPulse.Agent.exe</strong> onto the employee PC and run it.
              </li>
              <li className="leading-relaxed">
                <strong>Paste Setup Data:</strong> Click <em>"Copy Setup Data for Agent"</em> above, then in the Windows Agent window, click <em>"📋 Paste QR / Setup Data from Clipboard"</em>.
              </li>
              <li className="leading-relaxed">
                <strong>Instant Handshake:</strong> Click <em>"Pair & Activate Workstation"</em>. The agent stores its encrypted token and immediately starts streaming presence telemetry.
              </li>
            </ol>

            <div className="p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/20 text-[11px] text-blue-700 dark:text-blue-300">
              💡 <strong>Zero Spyware Guarantee:</strong> CK Pulse uses Win32 <code className="font-mono font-bold">GetLastInputInfo</code> to detect inactivity. It does NOT capture keystrokes, screen recordings, or passwords.
            </div>
          </div>
        )}

        {/* Download Agent Button */}
        <div className="pt-2 border-t border-black/5 dark:border-white/5 flex items-center justify-between text-xs">
          <span className="text-zinc-500 dark:text-zinc-400">Need the Windows Agent executable?</span>
          <a
            href={DOWNLOAD_LINKS.WINDOWS_EXE}
            download="CKPulse.Agent.exe"
            className="inline-flex items-center gap-1.5 font-bold text-blue-600 dark:text-blue-400 hover:underline"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Download CKPulse.Agent.exe</span>
          </a>
        </div>
      </div>
    </Modal>
  );
}
